/**
 * DocumentsPage Component
 * 
 * A React component that displays and manages documents for a specific trip.
 * Provides a file explorer style layout with document organization by category,
 * search functionality, and upload capabilities.
 * 
 * Features:
 * - File explorer layout with category organization
 * - Document search and filtering
 * - Upload facility integration
 * - Document metadata display
 * - Category-based grouping with icons
 * - Document counts per category
 * 
 * Component Structure:
 * - Header with search and upload functionality
 * - Category sections (Travel, Accommodation, Location, Planning)
 * - Document list with metadata display
 * - Upload integration
 * - Empty state display when no documents exist
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, useTrip, connectionRegistryService } from '@trip-planner-monorepo/shared';
import { FaSearch, FaUpload, FaFile, FaTrash, FaDownload, FaEye } from 'react-icons/fa';
import DocumentUpload from './DocumentUpload';
import UploadModal from './UploadModal';
import { configService, documentService } from '@trip-planner-monorepo/documents';
import './DocumentsPage.css';

function DocumentsPage() {
  // State Management
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [assocCounts, setAssocCounts] = useState(new Map()); // registry document_for counts by doc id
  
  // Authentication and Route Parameters
  const { currentUser } = useAuth();
  const { effectiveUserId } = useTrip();
  const { tripId } = useParams();
  const navigate = useNavigate();

  /**
   * Fetches all documents for the current trip
   * Called on component mount and after upload operations
   */
  const fetchDocuments = useCallback(async () => {
    if (!currentUser || !tripId) return;

    setLoading(true);
    try {
      console.log('Fetching documents for user:', effectiveUserId, 'trip:', tripId);

      // Use real document service to fetch documents
      const documents = await documentService.fetchDocuments(effectiveUserId, tripId);
      console.log('Documents fetched:', documents);

      setDocuments(documents);
      setError(''); // Clear any previous errors

      // On registry_only / dual_write trips the legacy document.connections[] may be empty;
      // get the registry document_for counts so the "Associated: N" badge is correct.
      try {
        const displayIndex = await connectionRegistryService.getRegistryDisplayIndex(effectiveUserId, tripId);
        setAssocCounts(displayIndex.documentAssocCount);
      } catch (regError) {
        console.warn('DocumentsPage: could not resolve association counts from registry', regError);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);

      // If it's a Firebase error about no documents, that's expected
      if (err.message.includes('Failed to get documents')) {
        setDocuments([]); // Set empty array instead of showing error
        setError(''); // Clear error
      } else {
        setError('Failed to load documents. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser, tripId, effectiveUserId]);

  // Initial data fetch on component mount
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Filter documents based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredDocuments(documents);
      // Debug logs
      console.log('DEBUG: documents.length:', documents.length);
      console.log('DEBUG: filteredDocuments.length:', documents.length);
      if (documents.length > 0) {
        console.log('DEBUG: first document:', documents[0]);
        console.log('DEBUG: first document categories:', documents[0].categories);
      }
      return;
    }

    const filtered = documents.filter(doc => 
      doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.categories.some(cat =>
        cat.id.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setFilteredDocuments(filtered);
    // Debug logs
    console.log('DEBUG: documents.length:', documents.length);
    console.log('DEBUG: filteredDocuments.length:', filtered.length);
    if (filtered.length > 0) {
      console.log('DEBUG: first document:', filtered[0]);
      console.log('DEBUG: first document categories:', filtered[0].categories);
    }
  }, [documents, searchTerm]);

  /**
   * Handles document upload completion
   * @param {Object} result - The upload result from DocumentUpload component
   */
  const handleDocumentProcessed = (result) => {
    console.log('Document processed:', result);
    fetchDocuments(); // Refresh the document list
  };

  /**
   * Formats file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  /**
   * Formats date for display
   * @param {string} dateString - ISO date string
   * @returns {string} Formatted date
   */
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  /**
   * Gets documents for a specific category
   * @param {string} categoryId - Category ID
   * @returns {Array} Array of documents in the category
   */
  const getDocumentsByCategory = (categoryId) => {
    return filteredDocuments.filter(doc => {
      const hasCategory = doc.categories.some(cat => cat.id === categoryId);

      // Legacy support for old document categories
      if (!hasCategory && categoryId === 'travel') {
        return doc.primaryCategory === 'flight';
      }

      return hasCategory;
    });
  };

  /**
   * Gets the primary category for a document
   * @param {Object} document - Document object
   * @returns {Object} Primary category object
   */
  const getPrimaryCategory = (document) => {
    return document.categories.find(cat => cat.isPrimary) || document.categories[0];
  };

  /**
   * Handles document deletion
   * @param {string} documentId - ID of document to delete
   */
  const handleDeleteDocument = async (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await documentService.deleteDocument(effectiveUserId, tripId, documentId);
        await fetchDocuments(); // Refresh the document list
      } catch (err) {
        setError('Failed to delete document. Please try again.');
        console.error('Error deleting document:', err);
      }
    }
  };

  /**
   * Handles document download
   * @param {Object} document - Document object
   */
  const handleDownloadDocument = (document) => {
    // TODO: Implement actual document download
    console.log('Downloading document:', document.name);
  };

  /**
   * Navigates to document details page
   * @param {Object} document - Document object
   */
  const handleViewDocument = (document) => {
    navigate(`/trips/${tripId}/documents/${document.firestoreId || document.id}`);
  };

  return (
    <div className="documents-page">
      {/* Header Section */}
      <div className="documents-header">
        <div className="documents-title">
          <h2>📄 Documents</h2>
          <p>Manage your travel documents and files</p>
        </div>
        
        <div className="documents-actions">
          <div className="search-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search documents..."
              className="search-input"
              style={{ paddingLeft: '40px' }}
            />
          </div>
          
          <button 
            onClick={() => setShowUploadModal(true)}
            className="upload-toggle-button"
          >
            <FaUpload />
            Upload Document
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && <div className="error-message">{error}</div>}

      {/* Upload Section (replaced by modal) */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onProcessed={(result) => {
            handleDocumentProcessed(result);
            setShowUploadModal(false);
          }}
        />
      )}

      {/* Documents Content */}
      {loading ? (
        <div className="loading">Loading documents...</div>
      ) : (
        <div className="documents-content">
          {filteredDocuments.length === 0 ? (
            <div className="no-documents">
              <FaFile className="no-documents-icon" />
              <p>No documents uploaded yet!</p>
              <p>Upload your first document to get started.</p>
            </div>
          ) : (
            <div className="documents-explorer">
              {configService.getCategories().map(category => {
                const categoryDocuments = getDocumentsByCategory(category.id);
                
                if (categoryDocuments.length === 0) return null;
                
                return (
                  <div key={category.id} className="category-section">
                    <div className="category-header">
                      <span className="category-icon">{category.icon}</span>
                      <h3 className="category-title">
                        {category.name} ({categoryDocuments.length})
                      </h3>
                    </div>
                    
                    <div className="documents-list">
                      {categoryDocuments.map(document => {
                        const primaryCategory = getPrimaryCategory(document);
                        const isPrimary = primaryCategory.isPrimary;
                        
                        return (
                          <div 
                            key={document.firestoreId || document.id} 
                            className="document-item"
                            onClick={() => handleViewDocument(document)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="document-info">
                              <div className="document-title">
                                <FaFile className="document-icon" />
                                <span className="document-name">{document.title}</span>
                                {!isPrimary && (
                                  <span className="secondary-category">
                                    Also in: {category.name}
                                  </span>
                                )}
                              </div>
                              
                              <div className="document-metadata">
                                <span className="upload-date">
                                  📅 Uploaded: {formatDate(document.uploadedAt)}
                                </span>
                                <span className="file-size">
                                  📄 Size: {formatFileSize(document.size)}
                                </span>
                                {(() => {
                                  // Prefer the registry document_for count; fall back to legacy
                                  // connections[] (and guard against an undefined connections field).
                                  const assocCount = assocCounts.get(document.id) ?? (document.connections?.length || 0);
                                  return assocCount > 0 ? (
                                    <span className="associations">
                                      Associated: {assocCount} item(s)
                                    </span>
                                  ) : null;
                                })()}
                              </div>
                              
                              {document.summary && (
                                <div className="document-summary">
                                  {document.summary}
                                </div>
                              )}
                            </div>
                            
                            <div className="document-actions">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewDocument(document);
                                }}
                                className="action-button preview-button"
                                title="View document details"
                              >
                                <FaEye />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadDocument(document);
                                }}
                                className="action-button download-button"
                                title="Download document"
                              >
                                <FaDownload />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteDocument(document.firestoreId || document.id);
                                }}
                                className="action-button delete-button"
                                title="Delete document"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DocumentsPage; 